import { HTTPError, TimeoutError } from "ky";

import type { CaptureException, SentryInstance } from "./sentryClient.ts";

import {
  isError,
  returnError,
  throwError,
  toUnknownError,
  UnknownError,
} from "./errorHandling.ts";
import { getSentry } from "./sentryClient.ts";

class FetchError extends Error {
  constructor(cause: Error) {
    super(cause.message);
    this.name = "FetchError";
    this.cause = cause;
  }
}

class ClientError extends HTTPError {
  constructor(cause: HTTPError) {
    super(cause.response, cause.request, cause.options);
    this.name = "ClientError";
  }
}

class AuthError extends ClientError {
  constructor(cause: HTTPError) {
    super(cause);
    this.name = "AuthError";
  }
}

class NotFoundError extends ClientError {
  constructor(cause: HTTPError) {
    super(cause);
    this.name = "NotFoundError";
  }
}

class ValidationError extends ClientError {
  constructor(cause: HTTPError) {
    super(cause);
    this.name = "ValidationError";
  }
}

class ServerError extends HTTPError {
  constructor(cause: HTTPError) {
    super(cause.response, cause.request, cause.options);
    this.name = "ServerError";
  }
}

type NetworkError =
  | AuthError
  | ClientError
  | FetchError
  | HTTPError
  | NotFoundError
  | ServerError
  | TimeoutError
  | UnknownError
  | ValidationError;

function isNetworkErrorStrict(
  error: unknown,
): error is
  | AuthError
  | ClientError
  | FetchError
  | NotFoundError
  | ServerError
  | UnknownError
  | ValidationError {
  return (
    error instanceof ServerError ||
    error instanceof ClientError ||
    error instanceof AuthError ||
    error instanceof NotFoundError ||
    error instanceof ValidationError ||
    error instanceof FetchError ||
    error instanceof UnknownError
  );
}

function isKyError(
  error: unknown,
): error is HTTPError | TimeoutError | TypeError {
  return (
    error instanceof HTTPError ||
    error instanceof TimeoutError ||
    (error instanceof Error &&
      (error.message === "Load failed" || error.message === "Failed to fetch"))
  );
}

function isNetworkError(error: unknown): error is NetworkError {
  return isNetworkErrorStrict(error) || isKyError(error);
}

/**
 * Returns specific error types depending on the response status
 */
function toNetworkError(error: unknown): NetworkError {
  // This makes the function idempotent, so it can be called multiple times without creating new errors
  if (isNetworkErrorStrict(error)) {
    return error;
  }

  // We can't handle non-ky errors more specifically atm
  if (!isKyError(error)) {
    return toUnknownError(error);
  }

  if (error instanceof TimeoutError) {
    return error;
  }

  if (error instanceof HTTPError) {
    const status = error.response.status;
    switch (true) {
      case status === 401 || status === 403:
        return new AuthError(error);
      case status === 404:
        return new NotFoundError(error);
      case status === 422:
        return new ValidationError(error);
      case status >= 400 && status <= 499:
        return new ClientError(error);
      case status >= 500 && status <= 599:
        return new ServerError(error);
    }
  }

  return new FetchError(error);
}

/**
 * Tries to minimize network error spam by capturing some errors as logs
 * toNetworkError + getSentry().capture[Exception|Log]
 */
type CaptureArgs = Parameters<CaptureException>;
const networkErrorToSentry = (
  error: CaptureArgs[0],
  hint?: CaptureArgs[1],
  getSentryInstance = getSentry,
) => {
  const networkError = toNetworkError(error);
  if (
    networkError instanceof ServerError ||
    networkError instanceof TimeoutError ||
    networkError instanceof FetchError
  ) {
    getSentryInstance().captureLog(networkError, hint);
    return;
  }

  if (networkError instanceof ClientError) {
    void networkError.response
      .json()
      .then((result) => {
        // @ts-expect-error This is just for error tracking in sentry
        networkError.responseJson = result;
      })
      .finally(() => {
        getSentryInstance().captureException(networkError, hint);
      });
    return;
  }

  getSentryInstance().captureException(networkError, hint);
};

/**
 * Returns thrown NetworkErrors
 * returnError + toNetworkError
 */
function returnNetworkError<Args extends unknown[], Return>(
  callback: (...args: Args) => Promise<Return>,
): (...args: Args) => Promise<NetworkError | Return> {
  const wrappedCallback = returnError(callback);
  return async (...args) => {
    const result = await wrappedCallback(...args);
    return isError(result) ? toNetworkError(result) : result;
  };
}

/**
 * Returns thrown NetworkErrors and sends them to Sentry
 * returnError + toNetworkError + networkErrorToSentry
 */
function handleNetwork<Args extends unknown[], Return>(
  callback: (...args: Args) => Promise<Return>,
  sentryInstance?: () => SentryInstance,
): (...args: Args) => Promise<NetworkError | Return> {
  const wrappedCallback = returnNetworkError(callback);
  return async (...args) => {
    const result = await wrappedCallback(...args);
    if (isError(result)) {
      networkErrorToSentry(result, undefined, sentryInstance);
    }
    return result;
  };
}

/**
 * Sends thrown Network errors to Sentry
 * returnError + toNetworkError + networkErrorToSentry + throwError
 */
function handleQuery<Args extends unknown[], Return>(
  callback: (...args: Args) => Promise<Return>,
  sentryInstance?: () => SentryInstance,
) {
  return throwError(handleNetwork(callback, sentryInstance));
}

export type { NetworkError };
export {
  AuthError,
  ClientError,
  FetchError,
  handleNetwork,
  handleQuery,
  HTTPError,
  isKyError,
  isNetworkError,
  networkErrorToSentry,
  NotFoundError,
  returnNetworkError,
  ServerError,
  TimeoutError,
  toNetworkError,
  UnknownError,
  ValidationError,
};
